/**
 * Booqable Date Picker - Custom Element
 * A modal date picker for selecting rental dates
 * Pure native JavaScript - no external dependencies
 * 
 * @element booqable-date-picker
 * @fires dates:selected - Dispatched when dates are applied
 * @fires dates:cleared - Dispatched when dates are cleared
 */

class BooqableDatePicker extends HTMLElement {
    constructor() {
        super();
        this.isOpen = false;
        this.startDate = null;
        this.endDate = null;
        this.selectingStart = true; // true = selecting start, false = selecting end
        this.currentMonthLeft = new Date();
        this.currentMonthRight = new Date();
        this.onApplyCallback = null;

        // Set right calendar to next month
        this.currentMonthRight.setMonth(this.currentMonthRight.getMonth() + 1);

        // Bound methods for event listeners
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    connectedCallback() {
        this.render();
        this.bindEvents();
    }

    disconnectedCallback() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Render the component
     */
    render() {
        this.innerHTML = `
            <div class="BooqableDatePicker__Overlay"></div>
            <div class="BooqableDatePicker__Modal">
                <div class="BooqableDatePicker__Header">
                    <span class="BooqableDatePicker__Title">Rental Dates</span>
                    <button type="button" class="BooqableDatePicker__CloseBtn" data-action="close" aria-label="Close">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M2 2L14 14M14 2L2 14"/>
                        </svg>
                    </button>
                </div>
                <div class="BooqableDatePicker__Body">
                    <div class="BooqableDatePicker__DateDisplay">
                        <div class="BooqableDatePicker__DateGroup ${this.selectingStart ? 'is-active' : ''}" data-select="start">
                            <span class="BooqableDatePicker__DateLabel">Start</span>
                            <span class="BooqableDatePicker__DateValue" data-date="start">Select date</span>
                        </div>
                        <span class="BooqableDatePicker__Arrow">→</span>
                        <div class="BooqableDatePicker__DateGroup ${!this.selectingStart ? 'is-active' : ''}" data-select="end">
                            <span class="BooqableDatePicker__DateLabel">Return</span>
                            <span class="BooqableDatePicker__DateValue" data-date="end">Select date</span>
                        </div>
                    </div>
                    <div class="BooqableDatePicker__Calendars">
                        <div class="BooqableDatePicker__Calendar" data-calendar="left"></div>
                        <div class="BooqableDatePicker__Calendar" data-calendar="right"></div>
                    </div>
                </div>
                <div class="BooqableDatePicker__Footer">
                    <button type="button" class="BooqableDatePicker__ClearBtn" data-action="clear">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M2 2L14 14M14 2L2 14"/>
                        </svg>
                        Clear dates
                    </button>
                    <button type="button" class="BooqableDatePicker__ApplyBtn" data-action="apply">
                        Apply
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * Render both calendars
     */
    renderCalendars() {
        const leftCalendar = this.querySelector('[data-calendar="left"]');
        const rightCalendar = this.querySelector('[data-calendar="right"]');

        if (leftCalendar) {
            leftCalendar.innerHTML = this.renderCalendar(this.currentMonthLeft, 'left');
        }
        if (rightCalendar) {
            rightCalendar.innerHTML = this.renderCalendar(this.currentMonthRight, 'right');
        }
    }

    /**
     * Render a single calendar month
     */
    renderCalendar(date, side) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const dayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

        // Get first day of month and total days
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();

        // Get day of week (0 = Sunday, adjust for Monday start)
        let startDayOfWeek = firstDay.getDay() - 1;
        if (startDayOfWeek < 0) startDayOfWeek = 6;

        // Get tomorrow's date for comparison (disable today and before)
        const tomorrow = new Date();
        tomorrow.setHours(0, 0, 0, 0);
        tomorrow.setDate(tomorrow.getDate() + 1);

        let html = `
            <div class="BooqableDatePicker__CalendarHeader">
                <button type="button" class="BooqableDatePicker__NavBtn" data-nav="prev" data-side="${side}" aria-label="Previous month">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M10 2L4 8L10 14"/>
                    </svg>
                </button>
                <span class="BooqableDatePicker__MonthYear">${monthNames[month]} ${year}</span>
                <button type="button" class="BooqableDatePicker__NavBtn" data-nav="next" data-side="${side}" aria-label="Next month">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M6 2L12 8L6 14"/>
                    </svg>
                </button>
            </div>
            <div class="BooqableDatePicker__DayNames">
                ${dayNames.map(day => `<span>${day}</span>`).join('')}
            </div>
            <div class="BooqableDatePicker__Days">
        `;

        // Empty cells before first day
        for (let i = 0; i < startDayOfWeek; i++) {
            html += `<span class="BooqableDatePicker__Day BooqableDatePicker__Day--empty"></span>`;
        }

        // Days of month
        for (let day = 1; day <= daysInMonth; day++) {
            const currentDate = new Date(year, month, day);
            currentDate.setHours(0, 0, 0, 0);

            const isDisabled = currentDate < tomorrow;
            const isStart = this.startDate && this.isSameDay(currentDate, this.startDate);
            const isEnd = this.endDate && this.isSameDay(currentDate, this.endDate);
            const isInRange = this.isInRange(currentDate);

            let classes = 'BooqableDatePicker__Day';
            if (isDisabled) classes += ' BooqableDatePicker__Day--disabled';
            if (isStart) classes += ' BooqableDatePicker__Day--start';
            if (isEnd) classes += ' BooqableDatePicker__Day--end';
            if (isInRange && !isStart && !isEnd) classes += ' BooqableDatePicker__Day--inRange';

            const dateStr = this.formatDateISO(currentDate);

            html += `<button type="button" class="${classes}" data-date="${dateStr}" ${isDisabled ? 'disabled' : ''}>${day}</button>`;
        }

        html += `</div>`;
        return html;
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        // Close button
        this.addEventListener('click', (e) => {
            const target = e.target;

            // Close button
            if (target.closest('[data-action="close"]')) {
                this.close();
                return;
            }

            // Overlay click
            if (target.classList.contains('BooqableDatePicker__Overlay')) {
                this.close();
                return;
            }

            // Clear button
            if (target.closest('[data-action="clear"]')) {
                this.clearDates();
                return;
            }

            // Apply button
            if (target.closest('[data-action="apply"]')) {
                this.applyDates();
                return;
            }

            // Navigation buttons
            const navBtn = target.closest('[data-nav]');
            if (navBtn) {
                const direction = navBtn.dataset.nav;
                const side = navBtn.dataset.side;
                this.navigateMonth(direction, side);
                return;
            }

            // Date selection
            const dateBtn = target.closest('[data-date]:not([disabled])');
            if (dateBtn && dateBtn.classList.contains('BooqableDatePicker__Day')) {
                this.selectDate(new Date(dateBtn.dataset.date));
                return;
            }

            // Date group selection (switch between start/end)
            const dateGroup = target.closest('[data-select]');
            if (dateGroup) {
                this.selectingStart = dateGroup.dataset.select === 'start';
                this.updateDateGroupStyles();
            }
        });

        // ESC key
        document.addEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Handle keyboard events
     */
    handleKeyDown(e) {
        if (e.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    /**
     * Navigate month
     */
    navigateMonth(direction, side) {
        const increment = direction === 'next' ? 1 : -1;

        if (side === 'left') {
            this.currentMonthLeft.setMonth(this.currentMonthLeft.getMonth() + increment);
            // Keep right calendar one month ahead if it would overlap
            if (this.currentMonthLeft >= this.currentMonthRight) {
                this.currentMonthRight = new Date(this.currentMonthLeft);
                this.currentMonthRight.setMonth(this.currentMonthRight.getMonth() + 1);
            }
        } else {
            this.currentMonthRight.setMonth(this.currentMonthRight.getMonth() + increment);
            // Keep left calendar one month behind if it would overlap
            if (this.currentMonthRight <= this.currentMonthLeft) {
                this.currentMonthLeft = new Date(this.currentMonthRight);
                this.currentMonthLeft.setMonth(this.currentMonthLeft.getMonth() - 1);
            }
        }

        this.renderCalendars();
    }

    /**
     * Select a date
     */
    selectDate(date) {
        if (this.selectingStart) {
            this.startDate = date;
            // If end date is before new start, clear it
            if (this.endDate && this.endDate < this.startDate) {
                this.endDate = null;
            }
            this.selectingStart = false;
        } else {
            // If selected date is before start, swap them
            if (date < this.startDate) {
                this.endDate = this.startDate;
                this.startDate = date;
            } else {
                this.endDate = date;
            }
            this.selectingStart = true;
        }

        this.updateDisplay();
        this.updateDateGroupStyles();
        this.renderCalendars();
    }

    /**
     * Update date group active styles
     */
    updateDateGroupStyles() {
        const startGroup = this.querySelector('[data-select="start"]');
        const endGroup = this.querySelector('[data-select="end"]');

        if (startGroup) {
            startGroup.classList.toggle('is-active', this.selectingStart);
        }
        if (endGroup) {
            endGroup.classList.toggle('is-active', !this.selectingStart);
        }
    }

    /**
     * Check if date is in selected range
     */
    isInRange(date) {
        if (!this.startDate || !this.endDate) return false;
        return date > this.startDate && date < this.endDate;
    }

    /**
     * Check if two dates are the same day
     */
    isSameDay(date1, date2) {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }

    /**
     * Format date as ISO string (YYYY-MM-DD)
     */
    formatDateISO(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Format date for display (DD-MM-YYYY)
     */
    formatDateDisplay(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }

    /**
     * Update the date display
     */
    updateDisplay() {
        const startDisplay = this.querySelector('[data-date="start"]');
        const endDisplay = this.querySelector('[data-date="end"]');

        if (startDisplay) {
            startDisplay.textContent = this.startDate
                ? this.formatDateDisplay(this.startDate)
                : 'Select date';
        }

        if (endDisplay) {
            endDisplay.textContent = this.endDate
                ? this.formatDateDisplay(this.endDate)
                : 'Select date';
        }
    }

    /**
     * Open the date picker modal
     */
    open(startDate = null, endDate = null, onApply = null) {
        this.onApplyCallback = onApply;

        // Set initial dates if provided
        if (startDate) {
            this.startDate = new Date(startDate);
            this.startDate.setHours(0, 0, 0, 0);
            // Set left calendar to start date's month
            this.currentMonthLeft = new Date(this.startDate);
            this.currentMonthRight = new Date(this.startDate);
            this.currentMonthRight.setMonth(this.currentMonthRight.getMonth() + 1);
        }
        if (endDate) {
            this.endDate = new Date(endDate);
            this.endDate.setHours(0, 0, 0, 0);
        }

        this.selectingStart = true;
        this.updateDisplay();
        this.updateDateGroupStyles();
        this.renderCalendars();

        this.isOpen = true;
        this.classList.add('is-open');
        document.body.style.overflow = 'hidden';
    }

    /**
     * Close the date picker modal
     */
    close() {
        this.isOpen = false;
        this.classList.remove('is-open');
        document.body.style.overflow = '';
    }

    /**
     * Clear selected dates
     */
    clearDates() {
        this.startDate = null;
        this.endDate = null;
        this.selectingStart = true;
        this.updateDisplay();
        this.updateDateGroupStyles();
        this.renderCalendars();

        this.dispatchEvent(new CustomEvent('dates:cleared', { bubbles: true }));
    }

    /**
     * Apply selected dates and update cart
     */
    async applyDates() {
        if (!this.startDate || !this.endDate) {
            this.close();
            return;
        }

        // Show loading state
        const applyBtn = this.querySelector('[data-action="apply"]');
        if (applyBtn) {
            applyBtn.disabled = true;
            applyBtn.textContent = 'Updating...';
        }

        try {
            // Update cart dates via API
            await this.updateCartDates();

            // Dispatch success event
            this.dispatchEvent(new CustomEvent('dates:selected', {
                bubbles: true,
                detail: {
                    startDate: this.startDate,
                    endDate: this.endDate
                }
            }));

            // Call optional callback
            if (this.onApplyCallback) {
                const momentLike = (date) => this.createMomentLike(date);
                this.onApplyCallback(momentLike(this.startDate), momentLike(this.endDate));
            }
        } catch (error) {
            console.error('[BooqableDatePicker] Failed to update cart dates:', error);
            // Could show error message here
        } finally {
            // Reset button
            if (applyBtn) {
                applyBtn.disabled = false;
                applyBtn.textContent = 'Apply';
            }
        }

        this.close();
    }

    /**
     * Update cart dates via Booqable API
     * PUT /api/1/cart
     */
    async updateCartDates() {
        // Get cart ID from various sources
        const cartId = this.getCartId();
        if (!cartId) {
            console.warn('[BooqableDatePicker] No cart ID found, skipping API update');
            return;
        }

        const config = window.BooqableConfig || {};
        const apiBaseUrl = config.apiBaseUrl || 'https://the-social-kitchen.booqableshop.com/api/1';
        const token = config.storefrontToken || '5e6ebf3d24ab26f8c300721e9ff8485e';
        const provider = config.provider || 'Shopify';

        // Format dates with default times (9:00 AM start, 5:30 PM end)
        const startsAt = new Date(this.startDate);
        startsAt.setHours(9, 0, 0, 0);
        const stopsAt = new Date(this.endDate);
        stopsAt.setHours(17, 30, 0, 0);

        const payload = {
            id: cartId,
            starts_at: startsAt.toISOString(),
            stops_at: stopsAt.toISOString(),
            data: {
                fulfillment_type: 'pickup'
            }
        };

        console.log('[BooqableDatePicker] Updating cart dates:', payload);

        const response = await fetch(`${apiBaseUrl}/cart`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'x-booqable-store-provider': provider,
                'x-booqable-storefront-token': token
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(`Failed to update cart: ${response.status} - ${JSON.stringify(error)}`);
        }

        const data = await response.json();
        console.log('[BooqableDatePicker] Cart dates updated successfully:', data);

        // Save cart ID to localStorage for persistence
        if (data.cart?.id) {
            localStorage.setItem('bqCartId', data.cart.id);
        }

        // Dispatch cart update event for other components to react
        this.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: data.cart }
        }));

        // Also notify the cart drawer to refresh
        const cartDrawer = document.querySelector('booqable-cart-drawer');
        if (cartDrawer?.refresh) {
            cartDrawer.refresh();
        }

        return data;
    }

    /**
     * Get cart ID from various sources
     */
    getCartId() {
        // Try cart drawer's data
        const cartDrawer = document.querySelector('booqable-cart-drawer');
        if (cartDrawer) {
            // Access via public getter if available
            const fullData = cartDrawer.fullCartData;
            if (fullData?.cart?.id) return fullData.cart.id;
        }

        // Try Booqable SDK
        if (window.Booqable?.cartData?.cartId) {
            return window.Booqable.cartData.cartId;
        }

        // Try localStorage
        const storedCartId = localStorage.getItem('booqable_cart_id');
        if (storedCartId) return storedCartId;

        return null;
    }

    /**
     * Create moment-like date wrapper for compatibility
     */
    createMomentLike(date) {
        const self = this;
        return {
            toDate: () => date,
            format: (fmt) => {
                if (fmt === 'YYYY-MM-DD') return self.formatDateISO(date);
                if (fmt === 'DD-MM-YYYY') return self.formatDateDisplay(date);
                if (fmt.includes('HH:mm:ss')) return date.toISOString();
                return self.formatDateDisplay(date);
            },
            clone: () => self.createMomentLike(new Date(date)),
            hour: (h) => { const d = new Date(date); d.setHours(h); return self.createMomentLike(d); },
            minute: (m) => { const d = new Date(date); d.setMinutes(m); return self.createMomentLike(d); },
            second: (s) => { const d = new Date(date); d.setSeconds(s); return self.createMomentLike(d); },
            millisecond: (ms) => { const d = new Date(date); d.setMilliseconds(ms); return self.createMomentLike(d); },
            toISOString: () => date.toISOString()
        };
    }

    /**
     * Get selected dates
     */
    getDates() {
        return {
            startDate: this.startDate,
            endDate: this.endDate
        };
    }
}

// Register custom element
if (!customElements.get('booqable-date-picker')) {
    customElements.define('booqable-date-picker', BooqableDatePicker);
}
